
/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import { withRouter } from 'react-router-dom'
import { Query, Mutation } from 'react-apollo'
import * as Sentry from '@sentry/browser'
import moment from 'moment'
import { isEqual } from 'lodash'

import {
  SvgIcon,
  Warning,
  Spinner,
  RequestMetaPanels,
  RequestProcessingStep,
  RequestProcessingSidebar,
  RequestProcessingSummary,
  RequestFileViewer,
  // CreateCaseModal,
  Modal,
  // RequestClassifyForm,
  RequestValidateModal,
  RequestInvalidateModal,
  RequestCancelModal,
  RequestCapture,
  // ToolbarObjectCases
} from '../components'

import {
  decodeBase64,
  encodeBase64
} from '../utils'

import {
  QUERY_REQUESTRUN,
  QUERY_FILERELATIONS
} from '../queries'

import { MUTATION_REQUESTRUN } from '../mutations'

import {
  FILETYPE_IDENTITY_VERIFICATION,
  REQUESTTYPE_IDENTITY_VERIFICATION,
  CONTENTTYPE_IDENTITY
} from '../constants'


const createCaseModalRef = React.createRef()
const reclassifyModalRef = React.createRef()
const invalidateModalRef = React.createRef()
const validateModalRef = React.createRef()
const cancelConfirmModalRef = React.createRef()
const showOriginalFileModalRef = React.createRef()
const casesToolBarRef = React.createRef()

const NETWORKSTATUS_POLL = 6
const NETWORKSTATUS_READY = 7

class Request extends React.Component {
  state = {
    showCreateCaseModal: false,
    showReclassifyModal: false,
    showValidateModal: false,
    showInvalidateModal: false,
    showCancelConfirmModal: false,
    showOriginalFileModal: false,
    language: null,
    requestType: null,
    validationNotes: null,
    holdChangeInProgress: false,
    reallocationInProgress: false,
    workflowInitiateInProgress: false,
    caseCreateTimestamp: null,
    refreshTimeStamp: null,
    pollCounter: 0,
    actionInProgress: false
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutsideModals)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutsideModals)
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (
      // if polling is taking place
      ((this.props.networkStatus === NETWORKSTATUS_POLL && nextProps.networkStatus === NETWORKSTATUS_READY) ||
        (this.props.networkStatus === NETWORKSTATUS_READY && nextProps.networkStatus === NETWORKSTATUS_POLL)
      ) &&
      // and the data is unchanged
      isEqual(this.props.data, nextProps.data)) {
      // don't'update component
      return false
    } else {
      return true
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // check if poll occurred and update counter if necessary
    if (prevProps.networkStatus === NETWORKSTATUS_POLL &&
      this.props.networkStatus === NETWORKSTATUS_READY) {
      this.setState(prevState => {
        const prevCountPlusOne = prevState.pollCounter + 1
        if (prevCountPlusOne === 30) {  // 30 equals 300s
          this.props.stopPolling()
        }
        return {
          pollCounter: prevCountPlusOne
        }
      })
    }
  }

  handleClickOutsideModals = (e) => {
    if (this.state.showCreateCaseModal && !createCaseModalRef.current.contains(e.target)) {
      // click is outside, close modal
      this.setState({ showCreateCaseModal: false })
    }

    if (this.state.showReclassifyModal && !reclassifyModalRef.current.contains(e.target)) {
      this.setState({ showReclassifyModal: false })
    }

    if (this.state.showInvalidateModal && !invalidateModalRef.current.contains(e.target)) {
      this.setState({ showInvalidateModal: false })
    }

    if (this.state.showValidateModal && !validateModalRef.current.contains(e.target)) {
      this.setState({ showValidateModal: false })
    }

    if (this.state.showCancelConfirmModal && !cancelConfirmModalRef.current.contains(e.target)) {
      this.setState({ showCancelConfirmModal: false })
    }

    if (this.state.showOriginalFileModal && !showOriginalFileModalRef.current.contains(e.target)) {
      this.setState({ showOriginalFileModal: false })
    }
  }

  currentProcessingStep = (requestRun) => {
    const { isAllocated, isCaptured, isValidated, validationRequired, isProcessed, processingRequired, content, status } = requestRun
    const captureRequired = content !== '{}'

    if (status.includes('CLOSED_')) {  // catches CLOSED_CANCELLED and CLOSED_DUPLICATE
      return 'closed'
    }

    if (isAllocated === false) {
      return 'allocate'
    }
    if (isCaptured === false && captureRequired) {
      return 'capture'
    }
    if (isValidated === false && validationRequired) {
      return 'validate'
    }
    if (processingRequired && (!isProcessed || status === 'PROCESSING')) {
      return 'workflow'
    }

    return null
  }

  nextProcessingStep = (requestRun) => {
    const { isAllocated, isValidated, validationRequired, isProcessing, processingRequired, content, status } = requestRun
    const captureRequired = content !== '{}'

    if (status.includes('CLOSED_')) {
      return '-'
    }

    if (isAllocated === false && captureRequired) {
      return 'capture'
    } else {
      if (isValidated === false && validationRequired) {
        return 'workflow'
      } else if (isProcessing || processingRequired) {
        return '-'
      }
    }

    return '-'
  }

  openCreateCaseModal = () => this.setState({ showCreateCaseModal: true })
  closeCreateCaseModal = () => this.setState({ showCreateCaseModal: false })

  // showCreateCaseModal = (requestRunId, unallocatedRequest) => (
  //   <CreateCaseModal
  //     forwardedRef={createCaseModalRef}
  //     followUp={true}
  //     caseRelations={[
  //       {
  //         objectId: decodeBase64(requestRunId),
  //         contentType: CONTENTTYPE_REQUESTRUN
  //       }
  //     ]}
  //     onCloseClick={this.closeCreateCaseModal}
  //     onSaveClick={this.closeCreateCaseModal}
  //     onCaseRelationsCreated={unallocatedRequest
  //       ? () => this.setState({ caseCreateTimestamp: moment().unix() })
  //       : null
  //     }
  //   />
  // )

  // showReclassifyModal = (requestRun, currentRequestType, requestrunMutate) => (
  //   <Modal
  //     ref={reclassifyModalRef}
  //     body={
  //       <div className='slds-m-left_small'>
  //         Request currently classified as <b>{currentRequestType.name}</b>.
  //         <br />
  //         <div className='slds-m-left_small slds-m-top_large'>
  //           <RequestClassifyForm
  //             language={this.state.language || requestRun.language}
  //             requestType={this.state.requestType || currentRequestType.id}
  //             onLanguageChange={this.handleRequestRunLanguageChoice}
  //             onRequestTypeChange={this.handleRequestTypeChoice}
  //             onClick={() => this.updateRequestClassification(requestrunMutate, requestRun.id, requestRun.language, requestRun.requesttype)}
  //           />
  //         </div>
  //       </div>
  //     }
  //   />
  // )

  showInvalidateModal = (requestRun, requestrunMutate) => (
    <RequestInvalidateModal
      forwardedRef={invalidateModalRef}
      requestRun={requestRun}
      requestrunMutate={requestrunMutate}
      closeModal={this.toggleInvalidateModal}
    />
  )

  showValidateModal = (requestRun, requestrunMutate, callRequestRunMethod) => (
    <RequestValidateModal
      forwardedRef={validateModalRef}
      requestRun={requestRun}
      requestrunMutate={requestrunMutate}
      callRequestRunMethod={callRequestRunMethod}
      closeModal={this.toggleValidateModal}
    />
  )

  showCancelConfirmModal = (requestRunId, requestrunMutate) => (
    <RequestCancelModal
      forwardedRef={cancelConfirmModalRef}
      requestRunId={requestRunId}
      requestrunMutate={requestrunMutate}
      closeModal={this.toggleCancelConfirmModal}
    />
  )

  showOriginalFileModal = (requestRunSources) => (
    <Modal
      ref={showOriginalFileModalRef}
      sizeLarge={true}
      body={
        <RequestFileViewer files={requestRunSources} />
      }
    />
  )

  updateRequestStatus = (requestRunId, currentRequestStatus, requestrunMutate) => {
    this.setState({ holdChangeInProgress: true })

    requestrunMutate({
      variables: {
        input: {
          id: requestRunId,
          status: currentRequestStatus === 'ON_HOLD'
            ? 'off_hold'
            : 'on_hold'
        }
      }
    })
    .then(() => this.setState({ holdChangeInProgress: false }))
    .catch(error => Sentry.captureMessage(`Failed to update request run status - ${error}`))
  }

  updateRequestClassification = (requestrunMutate, requestRunId, requestRunLanguage, requestRunRequestType) => {
    let input = {
      id: requestRunId
    }

    // set language, if changed
    if (this.state.language && requestRunLanguage !== this.state.language) {
      input.language = this.state.language
    }

    // set request type, if changed
    if (this.state.requestType && requestRunRequestType.id !== this.state.requestType) {
      input.requesttype = this.state.requestType
      this.callRequestRunMethod(requestRunId, requestrunMutate, 'unallocate')  // clear filerelations (if any) and capturedBy
        .then(() => {
          this.setRequestRunLanguageAndRequestType(requestrunMutate, requestRunId, input)
        })
    } else if (input.hasOwnProperty('language')) {
      this.setRequestRunLanguageAndRequestType(requestrunMutate, requestRunId, input)
    }

    this.toggleReclassifyModal()
  }

  setRequestRunLanguageAndRequestType = (requestrunMutate, requestRunId, input) => {
    requestrunMutate({ variables: { input } })
      .then(({ data }) => {
        requestrunMutate({
          variables: {
            input: {
              id: requestRunId,
              content: data.requestrunMutate.requestrun.requesttype.content
            }
          }
        })
        .then(() => this.setState({ refreshTimeStamp: moment.unix() }))
      })
      .catch(error => Sentry.captureMessage('Failed to update request run reqquest type and language'))
  }

  setRequestRunLanguage = (requestrunMutate, requestRunId, input) => {
    requestrunMutate({ variables: { input } })
      .then(() => this.setState({ refreshTimeStamp: moment.unix() }))
      .catch(error => Sentry.captureMessage('Failed to update request run language'))
  }

  handleRequestTypeChoice = (requestType) => this.setState({ requestType })
  handleRequestRunLanguageChoice = (language) => this.setState({ language })

  // toggleReclassifyModal = () => this.setState(prevState => ({ showReclassifyModal: !prevState.showReclassifyModal }))
  toggleInvalidateModal = () => this.setState(prevState => ({ showInvalidateModal: !prevState.showInvalidateModal }))
  toggleValidateModal = () => this.setState(prevState => ({ showValidateModal: !prevState.showValidateModal }))
  toggleCancelConfirmModal = () => this.setState(prevState => ({ showCancelConfirmModal: !prevState.showCancelConfirmModal }))
  toggleShowOriginalFileModal = () => this.setState(prevState => ({ showOriginalFileModal: !prevState.showOriginalFileModal }))

  callRequestRunMethod = (requestRunId, requestrunMutate, methodName) => {
    const input = {
      id: requestRunId,
      methodCall: methodName
    }

    return requestrunMutate({ variables: { input } })
      .catch(error => {
        this.setState({ actionInProgress: false })
        Sentry.captureMessage(`Failed to call method on request run - ${error.message}`)
      })
  }

  initiateWorkflowRun = (requestRunId, requestrunMutate, workflow, requestRunVersion, requestTypeName, identityName, workflowrunMutate) => {
    this.setState({ workflowInitiateInProgress: true })

    const input = {
      workflow,
      version: requestRunVersion,
      status: 'pending',
      parameters: JSON.stringify({ requestrun: decodeBase64(requestRunId) }),
      description: `${requestTypeName} for ${identityName}`
    }

    return workflowrunMutate({ variables: { input } })
      .then(({ data }) => {
        requestrunMutate({
          variables: {
            input: {
              id: requestRunId,
              workflowrunProcessing: data.workflowrunMutate.workflowrun.id
            }
          }
        })
        .then(() => this.setState({ workflowInitiateInProgress: false }))
        .catch(error => Sentry.captureMessage(`Failed to update Requestrun workflowrunProcessing - ${error}`))
      })
      .catch(error => Sentry.captureMessage(`Failed to initiate request workflow - ${error}`))
  }

  saveRequestAssignedTo = (requestRunMutate, requestRunId, assignedToIdentity) => {
    requestRunMutate({
      variables: {
        input: {
          id: requestRunId,
          assignedTo: assignedToIdentity
        }
      }
    })
    .catch(error => Sentry.captureMessage(`Failed to update request run assignedTo - ${error}`))
  }

  resetPolling = () => {
    this.setState({ pollCounter: 0 }, this.props.startPolling(10000))
  }

  render() {
    const { id: requestRunId, loading, error, data, refetch } = this.props
    if (loading) {
      return <Spinner type='tail' />
    }

    if (error) {
      return <Warning>Failed to fetch request data.</Warning>
    }

    let requestRun = data.requestrun
    let currentProcessingStep = this.currentProcessingStep(requestRun)

    // if (requestRun.identity !== null) {
    //   const identityRequestsRoute = `/os/identity/${requestRun.identity.id}/requests/${requestRunId}`
    //   const onIdentityPage = this.props.location.pathname === identityRequestsRoute
    //
    //   if (onIdentityPage === false) {
    //     return <Redirect to={identityRequestsRoute} />
    //   }
    // }

    const { isCaptured, capturedBy, validatedBy, status, identity, content, requesttype,
      assignedTo, origin, sources, isCancellable, isReclassifiable, isReallocateable,
      isRecapturable, isInvalidatable, isValidatable, isHoldable, isReleasable, isAwaitingDataFix,
      isResettable, isErrorIgnorable, isCurrentStepApprovable, isAwaitingWorkflowrunInitiation } = requestRun

    const noProcessingSidebar = false

    return (
      <Mutation mutation={MUTATION_REQUESTRUN}>
        {requestrunMutate => (
          <main className='stage-main slds-grid' style={{ flexDirection: 'column', height: `calc(100vh - 79px)` }}>  {/*${identity ? '215px' : '75px'})*/}
            {!identity &&
              <RequestRunHeader
                forwardedRef={casesToolBarRef}
                requestRunId={requestRunId}
                onCaseCreateClick={this.openCreateCaseModal}
                key={this.state.caseCreateTimestamp}
              />
            }
            <div>
              <RequestMetaPanels
                requestRunId={requestRunId}
                requestType={requesttype.name}
                requestAssignedTo={assignedTo}
                requestCapturedBy={capturedBy}
                requestValidatedBy={validatedBy}
                requestStatus={status}
                requestOrigin={origin}
                nextProcessingStep={this.nextProcessingStep(requestRun)}
                requestrunMutate={requestrunMutate}
              />
            </div>
            <section className='slds-grid slds-size_1-of-1' style={{ height: `calc(100vh - 163px)` }}>  {/* ${identity ? '297px' : '215px' */}
              <div className='slds-size_5-of-12 slds-border_right slds-scrollable_y' style={{ background: 'darkgrey' }}>
                {content !== '{}' &&  // checks if capture is required
                  isCaptured &&
                  currentProcessingStep !== 'validate'  // at validation step the file will show on the lefthand side and the capture form on the right

                  ? <CapturedForm
                      requestRun={requestRun}
                      className='slds-p-top_small u-bg-color-white slds-border_right'
                    />
                  : requesttype.id === REQUESTTYPE_IDENTITY_VERIFICATION  // hard-coded for now; this is only request type that breaks the mold
                    ? <Query
                        query={QUERY_FILERELATIONS}
                        variables={{
                          contentType: encodeBase64(CONTENTTYPE_IDENTITY, 'ContentTypeNode'),
                          objectId: decodeBase64(identity.id)
                        }}>

                        {({ loading, error, data }) => {
                          if (loading) {
                            return (
                              <div className='u-font-size-larger slds-text-align_center'>
                                <Spinner type='inverse' className='slds-is-relative slds-m-around_xx-large' />
                              </div>
                            )
                          }

                          if (error) {
                            return (
                              <div className='u-font-size-larger slds-p-around_large slds-text-color_inverse slds-text-align_center'>
                                <SvgIcon
                                  variant='utility'
                                  symbol='warning'
                                  colour='inverse'
                                  size='small'
                                  className='slds-m-right_small'
                                />
                                Failed to load identity-related files
                              </div>
                            )
                          }

                          const filerelations = data.filerelations.edges.filter(({ node: relation }) => (
                            relation.file.filetype.id === FILETYPE_IDENTITY_VERIFICATION  // NOTE: this could be a list of file types one would expect to contain appropriate photo that can be used for verification
                          ))
                          if (filerelations.length === 0) {
                            return (
                              <div className='u-font-size-larger slds-p-around_large slds-text-color_inverse slds-text-align_center'>
                                <SvgIcon
                                  variant='utility'
                                  symbol='info'
                                  colour='inverse'
                                  size='small'
                                  className='slds-m-right_small'
                                />
                                No previous identity files to compare against
                              </div>
                            )
                          }

                          return (
                            <RequestFileViewer
                              files={filerelations.map(({ node: relation }) => (
                                relation.file
                              ))}
                            />
                          )
                        }}
                      </Query>
                    : <RequestFileViewer files={sources} />
                }
              </div>
              <div className={`${noProcessingSidebar ? 'slds-size_7-of-12' : 'slds-size_5-of-12'} slds-p-around_x-small u-bg-color-white slds-scrollable_y`}>
                {currentProcessingStep === 'validate'
                  ? <CapturedForm requestRun={requestRun} />
                  : currentProcessingStep && currentProcessingStep !== 'closed'
                    ? <RequestProcessingStep
                        requestRun={requestRun}
                        step={currentProcessingStep}
                        requestrunMutate={requestrunMutate}
                        onCreateCaseClick={this.openCreateCaseModal}
                        saveRequestAssignedTo={this.saveRequestAssignedTo}
                        refetchData={refetch}
                        onWorkflowRefreshClick={this.resetPolling}
                        key={this.state.refreshTimeStamp}
                      />
                    : <RequestProcessingSummary requestRun={requestRun} className='slds-p-around_medium slds-m-top_xxx-small' />
                }
              </div>
              {!noProcessingSidebar &&
                <RequestProcessingSidebar
                  requestrunMutate={requestrunMutate}

                  // Section 1 - buttons that could be usable before workflowrun initiated
                  onReclassifyClick={isReclassifiable
                    ? this.toggleReclassifyModal
                    : null
                  }
                  onReallocateClick={isReallocateable
                    ? () => this.callRequestRunMethod(requestRunId, requestrunMutate, 'unallocate')  // not sure how the nav works if this was pressed
                    : null
                  }
                  onRecaptureClick={isRecapturable
                    ? () => this.callRequestRunMethod(requestRunId, requestrunMutate, 'prep_recapture')
                    : null
                  }
                  onInvalidateClick={isInvalidatable
                    ? () => {
                        this.callRequestRunMethod(requestRunId, requestrunMutate, 'invalidate')
                        this.toggleInvalidateModal()
                      }
                    : null
                  }
                  onValidateClick={isValidatable
                    ? this.toggleValidateModal
                    : null
                  }
                  onInitiateClick={
                    // () => {
                      // this.setState(
                      //   { actionInProgress: true },
                      //   () => this.callRequestRunMethod(requestRunId, requestrunMutate, 'initiate_awaiting_workflowrun')
                      //     .then(() => this.setState({ actionInProgress: false }))
                      // )
                    // }
                    isAwaitingWorkflowrunInitiation && this.state.actionInProgress === false
                    ? () => {
                        this.setState(
                          { actionInProgress: true },
                          () => this.callRequestRunMethod(requestRunId, requestrunMutate, 'initiate_awaiting_workflowrun')
                            .then(() => this.setState({ actionInProgress: false }))
                        )
                      }
                    : null
                  }

                  // Section 2 - buttons that could be usable whether workflowrun has been initiated or not
                  onHoldClick={isHoldable
                    ? () => this.callRequestRunMethod(requestRunId, requestrunMutate, 'place_on_hold')
                    : null
                  }
                  onReleaseClick={isReleasable
                    ? () => this.callRequestRunMethod(requestRunId, requestrunMutate, 'release_hold')
                    : null
                  }
                  onCancelClick={isCancellable
                    ? this.toggleCancelConfirmModal
                    : null
                  }

                  // Section 3 - buttons only usable after workflowrun has been initiated
                  onFixCapturingClick={isAwaitingDataFix
                    ? () => (
                        this.callRequestRunMethod(requestRunId, requestrunMutate, 'prep_data_fix')
                          .then(() => this.setState({ refreshTimeStamp: moment.unix() }))
                      )
                    : null
                  }
                  onRecheckErrorClick={isResettable
                    ? () => (
                        this.callRequestRunMethod(requestRunId, requestrunMutate, 'reset_requestrun')
                          .then(() => this.setState({ refreshTimeStamp: moment.unix() }))
                      )
                    : null
                  }
                  onIgnoreErrorClick={isErrorIgnorable
                    ? () => (
                        this.callRequestRunMethod(requestRunId, requestrunMutate, 'ignore_error')
                          .then(() => this.setState({ refreshTimeStamp: moment.unix() }))
                      )
                    : null
                  }
                  onMarkCurrentStepApprovedClick={isCurrentStepApprovable
                    ? () => (
                        this.callRequestRunMethod(requestRunId, requestrunMutate, 'approve_current_step')
                          .then(() => this.setState({ refreshTimeStamp: moment.unix() }))
                      )
                    : null
                  }

                  onViewOriginalFileClick={this.toggleShowOriginalFileModal}

                  reclassifyInProgress={this.state.showReclassifyModal}
                  // replace the states below with a mutationInProgress?
                  holdChangeInProgress={this.state.holdChangeInProgress}
                  initiateInProgress={this.state.workflowInitiateInProgress}
                  // userIsContentCaptor={userIsContentCaptor}
                  // requestrun specific values
                  isCaptured={requestRun.isCaptured}
                  isValidated={requestRun.isValidated}
                  processingRequired={requestRun.processingRequired}
                  isProcessing={requestRun.isProcessing}
                  sources={requestRun.sources}
                  status={requestRun.status}
                  userMayValidate={requestRun.userMayValidate}
                  userMayInvalidate={requestRun.userMayInvalidate}
                  requesttype={requestRun.requesttype}
                  version={requestRun.version}
                  identity={requestRun.identity}
                />
              }
            </section>
            {/* {this.state.showCreateCaseModal && this.showCreateCaseModal(requestRunId, identity === null)} */}
            {/* {this.state.showReclassifyModal && this.showReclassifyModal(requestRun, requesttype, requestrunMutate)} */}
            {this.state.showValidateModal && this.showValidateModal(requestRun, requestrunMutate, this.callRequestRunMethod)}
            {this.state.showInvalidateModal && this.showInvalidateModal(requestRun, requestrunMutate)}
            {this.state.showCancelConfirmModal && this.showCancelConfirmModal(requestRunId, requestrunMutate)}
            {this.state.showOriginalFileModal && this.showOriginalFileModal(sources)}
          </main>
        )}
      </Mutation>
    )
  }
}


const apiRoot = process.env.REACT_APP_API_ROOT
const templateRenderUrl = apiRoot.substr(0, apiRoot.lastIndexOf('/api'))

class CapturedForm extends React.Component {
  state = {
    templateRenderUrlResponse: null
  }

  componentDidMount() {
    fetch(`${templateRenderUrl}/${this.props.requestRun.asFileLive}`)
     .then(response => this.setState({ templateRenderUrlResponse: response }))
     .catch(error => console.error(error))
  }

  render() {
    const { requestRun, className } = this.props
    const { templateRenderUrlResponse } = this.state

    const requestType = requestRun.requesttype
    const hasTemplateToRender = requestRun.asFileLive && requestType.name !== 'Other'

    if (hasTemplateToRender) {
      if (templateRenderUrlResponse) {
        if (templateRenderUrlResponse.ok) {
          return (
            <iframe
              title='Captured form'
              src={`${templateRenderUrl}/${requestRun.asFileLive}`}
              height='100%'
              width='100%'
            />
          )
        } else {
          return (
            <RequestCapture
              requestRun={requestRun}
              requestType={requestType.id}
              open={true}
              updateRequestRun={() => null}
              singleItemCondensedView={true}
              editAllowedAfterInitialCapture={false}
              saveProgress={() => null}
              className={className}
            />
          )
        }
      } else {
        return <Spinner type='tail' />
      }
    } else {
      return (
        <RequestCapture
          requestRun={requestRun}
          requestType={requestType.id}
          open={true}
          updateRequestRun={() => null}
          singleItemCondensedView={true}
          editAllowedAfterInitialCapture={false}
          saveProgress={() => null}
          className={className}
        />
      )
    }
  }
}

class RequestRunHeader extends React.Component {
  render() {
    return (
      <header id='requestRunHeader' className='slds-grid'>
        <SvgIcon
          variant='utility'
          symbol='paste'
          colour='light'
          className='slds-p-left_medium slds-m-right_medium slds-p-top_x-small'
        />
        <div className='slds-m-top_small u-font-size-x-large u-text-caps slds-p-bottom_medium'>
          Request run
        </div>
        {/*<div className='slds-col_bump-left'>
          <ToolbarObjectCases
            ref={forwardedRef}
            objectId={requestRunId}
            elementId='requestRunHeader'
            onCaseCreateClick={onCaseCreateClick}
          />
        </div>*/}
      </header>
    )
  }
}

const RequestRunWrapper = ({ id, location, match }) => (
  <Query
    query={QUERY_REQUESTRUN}
    variables={{ id: id || match.params.requestRunId }}
    fetchPolicy='network-only'
    pollInterval={10000}  // 10s
    notifyOnNetworkStatusChange={true}>
    {({ loading, error, refetch, data, networkStatus, startPolling, stopPolling }) => (
      <Request
        id={id || match.params.id}
        // graphql specific props
        loading={loading}
        error={error}
        data={data}
        refetch={refetch}
        networkStatus={networkStatus}
        startPolling={startPolling}
        stopPolling={stopPolling}
        // router specific props
        location={location}
        match={match}
      />
    )}
  </Query>
)

export default withRouter(RequestRunWrapper)



// WEBPACK FOOTER //
// ./src/containers/Request.js